import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { n } from '../format'
import { VERDICTS } from '../compareSpec'

const ROW_H = 32
const HEADER_H = 36 // rows are offset by this so the sticky header never covers row 1
const OVERSCAN = 10
const GUTTER_W = 84
const GROUP_W = 96
const KEEP_W = 74
const FILE_W = 76
const STATUS_W = 98

/**
 * One cell of a comparison. A changed value shows the view's value and the
 * file's, in that order, because the view is the base and the arrow between them
 * reads as "becomes". Both are truncated by the column width, so the full text of
 * either side is always in the tooltip rather than only sometimes on screen.
 */
function DiffCell({ view, file }) {
  return (
    <>
      <span className="was">{view === '' ? '—' : view}</span>
      <span className="now">{file === '' ? '—' : file}</span>
    </>
  )
}

/**
 * Windowed table: only the rows on screen exist in the DOM, and only the pages
 * covering them exist in memory. Row count can be in the millions.
 *
 * In duplicate mode each row carries its group (`g`) and group size (`gs`);
 * alternating groups are shaded so the boundaries are obvious while scrolling.
 * Group 0 means "this key does not repeat", which is what deduplicated rows that
 * stood alone report.
 *
 * `showKeep` adds the verdict column: with every copy of a group listed, the one
 * resolution keeps (`w`) is the thing you actually want to see.
 *
 * `compare` switches to the diff layout. Rows then carry a verdict (`v`), which
 * side they came from (`side`), the uploaded file's values (`o`) and which columns
 * differ (`d`), and the table gains a second row-number column for the uploaded
 * file plus a status column. A row that exists on one side only carries that
 * side's values in `c`, so the base column layout never changes.
 */
export function DataTable({
  columns,
  widths,
  getRow,
  rowCount,
  ensureRange,
  done,
  view,
  showGroups,
  compare = false,
  // Whether a group spans several listed rows. It does where every copy is on
  // screen (duplicates, removed); it does not once each row already stands for a
  // whole record, and drawing blocks around single rows there is just noise.
  groupBlocks = false,
  showKeep = false,
  groupLabel = 'group',
  emptyMessage = 'No rows match these filters.',
}) {
  const scrollerRef = useRef(null)
  const frame = useRef(0)
  const [scrollTop, setScrollTop] = useState(0)
  const [viewport, setViewport] = useState(600)

  useLayoutEffect(() => {
    const el = scrollerRef.current
    if (!el) return
    const ro = new ResizeObserver(() => setViewport(el.clientHeight))
    ro.observe(el)
    setViewport(el.clientHeight)
    return () => ro.disconnect()
  }, [])

  const onScroll = useCallback((e) => {
    const top = e.currentTarget.scrollTop
    if (frame.current) return
    frame.current = requestAnimationFrame(() => {
      frame.current = 0
      setScrollTop(top)
    })
  }, [])

  const first = Math.max(0, Math.floor(scrollTop / ROW_H) - OVERSCAN)
  const last = Math.min(rowCount, first + Math.ceil(viewport / ROW_H) + OVERSCAN * 2)

  useEffect(() => {
    if (last > first) ensureRange(first, last)
  }, [first, last, ensureRange, rowCount])

  // A new filter or view shrinks the list under us; don't leave the user scrolled past the end.
  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: 0 })
    setScrollTop(0)
  }, [view])

  const statusLeft = GUTTER_W + (compare ? FILE_W : 0)
  const keepLeft = GUTTER_W + (showGroups ? GROUP_W : 0)
  const stickyW = GUTTER_W + (compare ? FILE_W + STATUS_W : 0) + (showGroups ? GROUP_W : 0) + (showKeep ? KEEP_W : 0)
  const totalW = stickyW + widths.reduce((a, b) => a + b, 0)
  const rows = []
  for (let i = first; i < last; i++) {
    const row = getRow(i)
    // Rows of a group are contiguous, so a group begins wherever the previous
    // row belonged to a different one. That boundary is what makes the groups
    // legible: it carries the dividing line and the only copy of the label.
    // An unloaded neighbour counts as a boundary — labelling the top row of a
    // half-scrolled window is better than leaving it anonymous.
    const prev = groupBlocks && row && i > first ? getRow(i - 1) : null
    const startsGroup = groupBlocks && row && (i === 0 || !prev || prev.g !== row.g)
    rows.push(
      <div
        className={
          'tr' +
          // Banding tracks a row across a wide table. Where groups span several
          // rows it marks the group boundaries, which is more useful than every
          // other row; otherwise it falls back to plain alternation, keyed on the
          // absolute index so a band never moves while scrolling.
          (groupBlocks
            ? row && row.g > 0 && row.g % 2 === 0
              ? ' alt'
              : ''
            : i % 2 === 1
              ? ' alt'
              : '') +
          (startsGroup && i > 0 ? ' group-start' : '') +
          // A copy the resolution drops reads as struck off the list rather than
          // as an equal row that happens to have a red verdict beside it.
          (showKeep && row && !row.w ? ' cut' : '') +
          // The verdict tints the whole row where the verdict is *about* the row
          // existing; a changed row is tinted per cell instead, since that is the
          // part that changed.
          (compare && row ? ` v-${row.v}` : '')
        }
        key={i}
        style={{ transform: `translateY(${i * ROW_H}px)`, width: totalW }}
      >
        <div className="td gutter" title={compare && row && row.i === null ? 'Not in your view' : undefined}>
          {row ? (row.i === null ? '·' : n(row.i + 1)) : ''}
        </div>
        {compare && (
          <div
            className="td gutter file-row"
            style={{ width: FILE_W, left: GUTTER_W }}
            title={row && row.j === null ? 'Not in the uploaded file' : undefined}
          >
            {row ? (row.j === null ? '·' : n(row.j + 1)) : ''}
          </div>
        )}
        {compare && (
          <div
            className={`td status${row ? ` v-${row.v}` : ''}`}
            style={{ width: STATUS_W, left: statusLeft }}
            title={
              row
                ? `${VERDICTS[row.v]?.hint ?? row.v}${
                    row.v === 'changed' ? ` — ${n(row.d.length)} column${row.d.length === 1 ? '' : 's'}` : ''
                  }${row.v === 'repeat' ? ` (${row.side === 'view' ? 'in your view' : 'in the uploaded file'})` : ''}`
                : ''
            }
          >
            {row && (
              <>
                <span className="status-mark" aria-hidden="true">
                  {VERDICTS[row.v]?.mark ?? '?'}
                </span>
                <span className="status-text">{VERDICTS[row.v]?.label ?? row.v}</span>
                {row.v === 'changed' && <span className="status-count">{n(row.d.length)}</span>}
              </>
            )}
          </div>
        )}
        {showGroups && (
          <div
            className={`td group${row && row.g > 0 && groupBlocks ? ' in-group' : ''}`}
            style={{ width: GROUP_W, left: GUTTER_W }}
            title={
              row
                ? row.g > 0
                  ? groupBlocks
                    ? `Group ${n(row.g)} — ${n(row.gs)} rows share this key`
                    : `This record stands for ${n(row.gs)} rows that share the key`
                  : 'This key does not repeat'
                : ''
            }
          >
            {row ? (
              row.g > 0 ? (
                !groupBlocks ? (
                  // One row per record already: the group number is bookkeeping,
                  // but how many rows it speaks for is the useful part.
                  <span className="group-size solo">×{n(row.gs)}</span>
                ) : startsGroup ? (
                  <>
                    <span className="group-id">#{n(row.g)}</span>
                    <span className="group-size">×{n(row.gs)}</span>
                  </>
                ) : (
                  // Repeating the number on every row of a group turns a block
                  // into noise; the spine and the shading already say "same group".
                  <span className="group-cont" aria-hidden="true">
                    ⋮
                  </span>
                )
              ) : (
                <span className="group-none">—</span>
              )
            ) : (
              ''
            )}
          </div>
        )}
        {showKeep && (
          <div
            className={`td keep${row ? (row.w ? ' kept' : ' cut') : ''}`}
            style={{ width: KEEP_W, left: keepLeft }}
            title={row ? (row.w ? 'The copy resolution keeps' : 'Removed as a duplicate of the kept copy') : ''}
          >
            {row ? (row.w ? '✓ keep' : '✗ drop') : ''}
          </div>
        )}
        {row
          ? columns.map((col, ci) => {
              // A merged record marks the cells it took from another copy, so a
              // value that came from elsewhere is never presented as original.
              const filled = row.m?.includes(col.index)
              const value = row.c?.[col.index] ?? ''
              const differs = compare && row.d?.includes(col.index)
              if (differs) {
                const other = row.o?.[col.index] ?? ''
                return (
                  <div
                    className="td diff"
                    key={col.name}
                    style={{ width: widths[ci] }}
                    title={`${col.name}\nview: ${value === '' ? '(blank)' : value}\nfile: ${other === '' ? '(blank)' : other}`}
                  >
                    <DiffCell view={value} file={other} />
                  </div>
                )
              }
              return (
                <div
                  className={
                    'td' +
                    (filled ? ' merged' : '') +
                    // Numbers only line up if they are ranged right.
                    (col.kind === 'number' ? ' num' : '') +
                    // An empty cell has to look different from one still loading,
                    // and in a tool about filling blanks it is worth seeing.
                    (value === '' ? ' blank' : '')
                  }
                  key={col.name}
                  style={{ width: widths[ci] }}
                  title={
                    filled
                      ? `${value} — filled in from another copy of this record`
                      : value === ''
                        ? 'empty'
                        : value
                  }
                >
                  {value}
                </div>
              )
            })
          : columns.map((col, ci) => (
              <div className="td skeleton" key={col.name} style={{ width: widths[ci] }}>
                <span />
              </div>
            ))}
      </div>,
    )
  }

  return (
    <div className="table-scroller" ref={scrollerRef} onScroll={onScroll}>
      <div className="table-inner" style={{ width: totalW, height: rowCount * ROW_H + HEADER_H }}>
        <div className="thead" style={{ width: totalW, height: HEADER_H }}>
          <div className="th gutter" title={compare ? 'Row number in your view' : undefined}>
            row
          </div>
          {compare && (
            <div className="th gutter file-row" style={{ width: FILE_W, left: GUTTER_W }} title="Row number in the uploaded file">
              file
            </div>
          )}
          {compare && (
            <div className="th status" style={{ width: STATUS_W, left: statusLeft }}>
              vs view
            </div>
          )}
          {showGroups && (
            <div className="th group" style={{ width: GROUP_W, left: GUTTER_W }}>
              {groupLabel}
            </div>
          )}
          {showKeep && (
            <div className="th keep" style={{ width: KEEP_W, left: keepLeft }}>
              keep?
            </div>
          )}
          {columns.map((col, ci) => (
            <div
              className={`th${col.kind === 'number' ? ' num' : ''}`}
              key={col.name}
              style={{ width: widths[ci] }}
              title={`${col.name} — ${col.kind}`}
            >
              <span className="th-name">{col.name}</span>
              {col.kind === 'number' && <span className="th-kind">123</span>}
            </div>
          ))}
        </div>
        {rows}
      </div>
      {rowCount === 0 && <div className="empty">{!done ? 'Scanning…' : emptyMessage}</div>}
    </div>
  )
}

export { ROW_H, HEADER_H, GUTTER_W, GROUP_W, KEEP_W, FILE_W, STATUS_W }
